"use client";

import { memo } from "react";
import { useTranslations } from "next-intl";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { ACTIVITY_CHART_COLORS } from "./constants";
import { ActivityTooltip } from "./ActivityTooltip";

interface AreaChartContentProps {
  chartData: any[];
  maxValue: number;
  yTicks: number[];
  xTicks: number[];
  normalThreshold: number;
  hideYAxis: boolean;
  displayMode?: "minutes" | "questions";
  height?: number;
}

/**
 * Содержимое Area графика (область)
 */
export const AreaChartContent = memo(function AreaChartContent({
  chartData,
  maxValue,
  yTicks,
  xTicks,
  normalThreshold,
  hideYAxis,
  displayMode = "minutes",
  height = 300,
}: AreaChartContentProps) {
  const t = useTranslations();

  return (
    <div style={{ width: "100%", height }}>
      <ResponsiveContainer width="100%" height="100%" minHeight={height}>

        <AreaChart
          data={chartData}
          margin={{ top: 10, right: 10, left: 10, bottom: 20 }}
        >
          <defs>
            <linearGradient id="colorNormal" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={ACTIVITY_CHART_COLORS.normal} stopOpacity={0.6} />
              <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.6} />
            </linearGradient>
            <linearGradient id="colorExceeded" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#ef4444" stopOpacity={0.6} />
              <stop offset="100%" stopColor="#dc2626" stopOpacity={0.6} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={ACTIVITY_CHART_COLORS.grid}
            vertical={false}
          />
          <XAxis
            dataKey="minute"
            tick={{ fontSize: 11, fill: ACTIVITY_CHART_COLORS.axis }}
            tickLine={false}
            axisLine={{ stroke: ACTIVITY_CHART_COLORS.grid }}
            ticks={xTicks}
            label={{
              value: displayMode === "questions" ? t("label-questions") : t("label-minutes"),
              position: "bottom",
              offset: 5,
              fontSize: 12,
              fill: ACTIVITY_CHART_COLORS.axis,
            }}
          />
          <YAxis
            tick={hideYAxis ? false : { fontSize: 11, fill: ACTIVITY_CHART_COLORS.axis }}
            tickLine={false}
            axisLine={false}
            domain={[0, maxValue]}
            ticks={hideYAxis ? [] : yTicks}
            width={hideYAxis ? 0 : 30}
          />
          {/* Линия порога нормы */}
          <ReferenceLine
            y={normalThreshold}
            stroke={ACTIVITY_CHART_COLORS.exceeded}
            strokeDasharray="5 5"
            strokeOpacity={0.5}
          />
          <Tooltip
            content={<ActivityTooltip displayMode={displayMode} />}
            cursor={{ stroke: ACTIVITY_CHART_COLORS.normal, strokeWidth: 1 }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke="transparent"
            strokeWidth={0}
            fill="url(#colorNormal)"
            dot={false}
            activeDot={{ r: 6, fill: ACTIVITY_CHART_COLORS.normal, stroke: "#fff", strokeWidth: 2 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
});
